import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageSquare } from 'lucide-react'
import { ConversationSidebar } from '@/components/chat/conversation-sidebar'
import { ChatArea } from '@/components/chat/chat-area'
import { AgentSelectDialog } from '@/components/chat/agent-select-dialog'
import { useChatStore } from '@/stores/chat-store'
import { useOrganizationStore } from '@/stores/organization-store'

export function ChatPage() {
  const { t } = useTranslation('chat')
  const currentProject = useOrganizationStore((s) => s.currentProject)

  const {
    conversations,
    isLoadingConversations,
    activeConversation,
    messages,
    isLoadingMessages,
    isSending,
    error,
    fetchConversations,
    createConversation,
    selectConversation,
    sendMessage,
    deleteConversation,
    clearError,
    reset,
  } = useChatStore()

  const [showAgentDialog, setShowAgentDialog] = useState(false)

  // Fetch conversations when project changes
  useEffect(() => {
    if (currentProject?.id) {
      reset()
      fetchConversations(currentProject.id)
    }
  }, [currentProject?.id, fetchConversations, reset])

  const handleNewConversation = useCallback(() => {
    setShowAgentDialog(true)
  }, [])

  const handleAgentSelect = useCallback(
    async (agentId: string) => {
      if (!currentProject?.id) return
      await createConversation(currentProject.id, agentId)
    },
    [currentProject?.id, createConversation]
  )

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (!currentProject?.id) return
      clearError()
      selectConversation(currentProject.id, conversationId)
    },
    [currentProject?.id, selectConversation, clearError]
  )

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!currentProject?.id) return
      sendMessage(currentProject.id, content)
    },
    [currentProject?.id, sendMessage]
  )

  const handleDeleteConversation = useCallback(
    (conversationId: string) => {
      if (!currentProject?.id) return
      deleteConversation(currentProject.id, conversationId)
    },
    [currentProject?.id, deleteConversation]
  )

  // No project selected
  if (!currentProject) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
        <div className="text-center">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-500 mb-4" />
          <p className="text-sm text-gray-400">{t('errors.noProject')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-6">
      <ConversationSidebar
        conversations={conversations}
        activeConversationId={activeConversation?.id || null}
        isLoading={isLoadingConversations}
        onNewConversation={handleNewConversation}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
      />

      <ChatArea
        conversation={activeConversation}
        messages={messages}
        isSending={isSending}
        isLoadingMessages={isLoadingMessages}
        error={error}
        onSendMessage={handleSendMessage}
      />

      <AgentSelectDialog
        open={showAgentDialog}
        onOpenChange={setShowAgentDialog}
        projectId={currentProject.id}
        onSelect={handleAgentSelect}
      />
    </div>
  )
}
